import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRing, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, pushPermission, enablePush, disablePush } from "@/lib/push";

const CAT = { appointments: "Rendez-vous", quotes: "Devis", invoices: "Factures & paiements", emails: "Emails", clients: "Clients", tasks: "Tâches", contracts: "Contrats & interventions", waitlist: "Liste d'attente", satisfaction: "Satisfaction / avis", loyalty: "Fidélité / parrainage", team: "Équipe", accounting: "Comptabilité", integrations: "Intégrations / erreurs", subscription: "Abonnement" };

export default function NotificationSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushState, setPushState] = useState("checking");

  const load = async () => {
    setLoading(true);
    try { setData((await api.get("/notifications/preferences")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    if (!pushSupported()) setPushState("unsupported");
    else setPushState(pushPermission() === "granted" ? "granted" : pushPermission() === "denied" ? "denied" : "default");
  }, []);

  const save = async (next) => {
    setSaving(true);
    try { setData((await api.put("/notifications/preferences", { prefs: next.prefs, settings: next.settings })).data); toast.success("Préférences enregistrées"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const setCat = (c, key, val) => { const p = { ...data.prefs, [c]: { ...data.prefs[c], [key]: val } }; setData({ ...data, prefs: p }); };
  const setSetting = (k, v) => setData({ ...data, settings: { ...data.settings, [k]: v } });
  const allOn = () => setData({ ...data, prefs: Object.fromEntries(Object.keys(data.prefs).map((c) => [c, { in_app: true, push: true }])) });
  const allOff = () => setData({ ...data, prefs: Object.fromEntries(Object.keys(data.prefs).map((c) => [c, { in_app: false, push: false }])) });

  const doEnablePush = async () => {
    try { await enablePush(); setPushState("granted"); await api.post("/push/test"); toast.success("Notifications push activées sur cet appareil"); }
    catch (e) { const m = e.message; setPushState(m === "denied" ? "denied" : m === "unsupported" ? "unsupported" : "error"); toast.error(m === "denied" ? "Autorisation refusée" : m === "not_configured" ? "Push non configuré côté serveur" : "Push non pris en charge sur cet appareil"); }
  };
  const doDisablePush = async () => { await disablePush(); setPushState("default"); toast.success("Désabonné de cet appareil"); };

  if (loading || !data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const pushLabel = { checking: "Vérification…", granted: "Notifications activées", denied: "Autorisation refusée", default: "Non configurées", unsupported: "Non prises en charge sur cet appareil", error: "Erreur" }[pushState];

  return (
    <div>
      <PageHeader title="Préférences de notifications" subtitle="Choisissez comment vous êtes prévenu — par utilisateur et par appareil" icon={BellRing} />

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="push-card">
        <div className="flex items-center gap-3">
          <Smartphone className="w-5 h-5 text-emerald-600" />
          <div className="flex-1"><div className="text-sm font-semibold text-slate-800">Notifications push (cet appareil)</div><div className="text-xs text-slate-500" data-testid="push-state">{pushLabel}</div></div>
          {pushState === "granted" ? <Button size="sm" variant="outline" onClick={doDisablePush} className="border-slate-200">Désactiver</Button>
            : (pushState === "default" || pushState === "error") ? <Button size="sm" onClick={doEnablePush} data-testid="enable-push-btn" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Activer les notifications</Button>
            : null}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button size="sm" variant="outline" onClick={allOn} className="border-slate-200">Tout activer</Button>
        <Button size="sm" variant="outline" onClick={allOff} className="border-slate-200">Tout désactiver</Button>
        <Button size="sm" variant="outline" onClick={load} className="border-slate-200">Réinitialiser</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2.5 bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold">
          <span>Catégorie</span><span>Dans l'app</span><span>Push</span>
        </div>
        {(data.categories || Object.keys(data.prefs)).map((c) => (
          <div key={c} data-testid={`notifpref-${c}`} className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-3 border-t border-slate-100 items-center">
            <span className="text-sm text-slate-700">{CAT[c] || c}</span>
            <input type="checkbox" checked={!!data.prefs[c]?.in_app} onChange={(e) => setCat(c, "in_app", e.target.checked)} data-testid={`notifpref-${c}-inapp`} className="accent-emerald-500 w-4 h-4 justify-self-center" />
            <input type="checkbox" checked={!!data.prefs[c]?.push} onChange={(e) => setCat(c, "push", e.target.checked)} data-testid={`notifpref-${c}-push`} className="accent-emerald-500 w-4 h-4 justify-self-center" />
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 mb-5">
        <h3 className="text-sm font-semibold text-slate-800">Rendez-vous & rappels</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">Récap du lendemain à</span>
          <Select value={String(data.settings.reminder_hour)} onValueChange={(v) => setSetting("reminder_hour", Number(v))}>
            <SelectTrigger data-testid="reminder-hour" className="w-28 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200 max-h-[40vh]">{Array.from({ length: 24 }, (_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}h00</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="remind-before">
          <input type="checkbox" checked={!!data.settings.remind_before_enabled} onChange={(e) => setSetting("remind_before_enabled", e.target.checked)} className="accent-emerald-500" />
          Rappel avant chaque RDV (désactivé par défaut)
        </label>
        {data.settings.remind_before_enabled && (
          <Select value={String(data.settings.remind_before_hours)} onValueChange={(v) => setSetting("remind_before_hours", Number(v))}>
            <SelectTrigger className="w-40 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">{[1, 2, 3, 6].map((h) => <SelectItem key={h} value={String(h)}>{h} heure(s) avant</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 mb-5">
        <h3 className="text-sm font-semibold text-slate-800">Suivi des emails</h3>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!data.settings.email_open_track} onChange={(e) => setSetting("email_open_track", e.target.checked)} data-testid="email-open-track" className="accent-emerald-500" /> Me prévenir des ouvertures de devis/factures (désactivé par défaut)</label>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!data.settings.email_click_track} onChange={(e) => setSetting("email_click_track", e.target.checked)} className="accent-emerald-500" /> Me prévenir des clics sur les liens (désactivé par défaut)</label>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">Email important non ouvert après</span>
          <Select value={String(data.settings.unopened_days)} onValueChange={(v) => setSetting("unopened_days", Number(v))}>
            <SelectTrigger data-testid="unopened-days" className="w-28 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">{[1, 2, 3, 5, 7].map((d) => <SelectItem key={d} value={String(d)}>{d} jour(s)</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-400">Les erreurs importantes (non délivré, rejeté) sont activées par défaut. Les simples « délivré » ne déclenchent jamais de push.</p>
      </div>

      <Button onClick={() => save(data)} disabled={saving} data-testid="notifpref-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
    </div>
  );
}
